import React from 'react';
import { roundToDecimals } from 'utils/shared/number/roundToDecimals';

interface StrategyMetrics {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
}

interface StrategyValues {
  strategyMetrics?: StrategyMetrics;
}

interface Props {
  strategyValues: StrategyValues;
  volatility?: number;
}

export const StrategyGreeks: React.FC<Props> = ({ strategyValues, volatility }) => {
  const strategyMetrics = strategyValues?.strategyMetrics;

  if (!strategyMetrics) {
    return null;
  }

  const delta = strategyMetrics?.delta;
  const gamma = strategyMetrics?.gamma;
  const theta = strategyMetrics?.theta;
  const vega = strategyMetrics?.vega;
  const rho = strategyMetrics?.rho;

  return (
    <div className="mt-2">
      <div className="mx-auto max-w-7xl rounded-md bg-palette-black-2 py-2.5">
        <div className="grid grid-cols-2 items-center justify-evenly gap-2 text-[10px] md:grid-cols-3 lg:grid-cols-6">
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Delta</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-top-position" aria-label="Delta">
                Δ
              </span>
              <span className="tracking-tight text-white">
                {delta !== undefined && delta !== null ? roundToDecimals(delta, 2) : '-'}
              </span>
            </p>
          </div>
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Gamma</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-graph-red" aria-label="Gamma">
                Γ
              </span>
              <span className="tracking-tight text-white">
                {gamma !== undefined && gamma !== null ? roundToDecimals(gamma, 4) : '-'}
              </span>
            </p>
          </div>
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Theta</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-graph-purple" aria-label="Theta">
                Θ
              </span>
              <span className="tracking-tight text-white">
                {theta !== undefined && theta !== null ? roundToDecimals(theta, 2) : '-'}
              </span>
            </p>
          </div>
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Vega</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-graph-yellow" aria-label="Vega">
                ν
              </span>
              <span className="tracking-tight text-white">
                {vega !== undefined && vega !== null ? roundToDecimals(vega, 2) : '-'}
              </span>
            </p>
          </div>
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Rho</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-brand-green-primary" aria-label="Rho">
                ρ
              </span>
              <span className="tracking-tight text-white">
                {rho !== undefined && rho !== null ? roundToDecimals(rho, 2) : '-'}
              </span>
            </p>
          </div>
          <div className="space-y-1 text-center">
            <p className="mb-0.5">Volatility</p>
            <p className="flex items-center justify-center gap-x-2">
              <span className="text-base text-electric-teal" aria-label="Volatility">
                σ
              </span>
              <span className="tracking-tight text-white">
                {volatility !== undefined && volatility !== null
                  ? `${roundToDecimals(volatility * 100, 2)}%`
                  : '-'}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
